/* eslint-disable import/no-default-export,jsx-a11y/label-has-associated-control,jsx-a11y/no-onchange,react/no-danger */
import classNames from 'classnames';
import {
  ButtonSizes,
  PrimaryButton,
} from 'app/common/components/controls/button/button';
import { TeamType } from 'app/gamma/src/modules/state/models/teams';
import React from 'react';
import { EnterpriseModel } from 'app/gamma/src/types/models';
import preventDefault from 'app/gamma/src/util/prevent-default';
import { byAttributeCaseInsensitive } from 'app/gamma/src/util/sort';
import styles from './create-team.less';

import { TeamTestIds } from '@trello/webdriverio-tests';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import { dontUpsell } from '@trello/config';

const formatBody = forTemplate('org_create');
const formatProduct = forNamespace('products');

const idPrefix = `${Date.now()}-`; // for id specificity

interface CreateTeamProps {
  teamType: TeamType;
  enterprises?: EnterpriseModel[];
  submitCreateTeam: (
    type: TeamType,
    displayName: string,
    desc?: string,
    enterprise?: string,
    redirectAfterCreate?: boolean,
  ) => void;
}

interface CreateTeamState {
  displayName: string;
  desc?: string;
  enterprise?: string;
  isSubmitting: boolean;
}

class CreateTeam extends React.Component<CreateTeamProps, CreateTeamState> {
  state = {
    displayName: '',
    desc: undefined,
    enterprise:
      this.props.teamType === TeamType.Enterprise &&
      this.props.enterprises &&
      this.props.enterprises.length
        ? this.props.enterprises[0].id
        : undefined,
    isSubmitting: false,
  };

  componentDidMount() {
    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.CREATE_TEAM_MENU,
      context: {
        teamType: this.props.teamType,
      },
    });
  }

  onChangeTeamName: React.FormEventHandler<HTMLInputElement> = (e) => {
    this.setState({
      displayName: e.currentTarget.value,
    });
  };

  onChangeEnterprise: React.FormEventHandler<HTMLSelectElement> = (e) => {
    this.setState({
      enterprise: e.currentTarget.value,
    });
  };

  onChangeTeamDescription: React.FormEventHandler<HTMLTextAreaElement> = (
    e,
  ) => {
    this.setState({
      desc: e.currentTarget.value,
    });
  };

  onCreateTeam: React.FormEventHandler<HTMLFormElement> = (e) => {
    this.setState({ isSubmitting: true });
    const { teamType } = this.props;
    const { displayName, desc, enterprise } = this.state;
    const redirectAfterCreate = !!enterprise;
    this.props.submitCreateTeam(
      teamType,
      displayName.trim(),
      desc,
      enterprise,
      redirectAfterCreate,
    );
  };

  onBusinessLinkClick(e: React.MouseEvent<HTMLAnchorElement>) {
    // TODO: track this link click
  }

  getEnterprises = () => {
    const { enterprises } = this.props;
    if (!enterprises || !enterprises.length) {
      return undefined;
    }

    return enterprises.length === 1 ? (
      <p id={`${idPrefix}create-team-enterprise-name`}>
        {enterprises[0].displayName}
      </p>
    ) : (
      <select
        className={styles.select}
        id={`${idPrefix}create-team-enterprise-name`}
        onChange={this.onChangeEnterprise}
      >
        {enterprises
          .sort(byAttributeCaseInsensitive('displayName'))
          .map((ent, index) => {
            const { id, displayName } = ent;

            return (
              <option aria-selected={index === 0} value={id} key={id}>
                {displayName}
              </option>
            );
          })}
      </select>
    );
  };

  render() {
    const { displayName, isSubmitting } = this.state;
    const { teamType } = this.props;

    return (
      <form onSubmit={preventDefault(this.onCreateTeam)}>
        <label htmlFor={`${idPrefix}create-team-org-display-name`}>
          {formatBody('name')}
        </label>
        <input
          autoFocus={true}
          className={styles.textInput}
          dir="auto"
          id={`${idPrefix}create-team-org-display-name`}
          maxLength={100}
          onChange={this.onChangeTeamName}
          tabIndex={0}
          type="text"
          data-test-id={TeamTestIds.CreateTeamNameInput}
        />
        {teamType === TeamType.Enterprise ? (
          <>
            <label htmlFor={`${idPrefix}create-team-enterprise-name`}>
              {formatProduct('enterprise')}
            </label>
            {this.getEnterprises()}
          </>
        ) : (
          <>
            <label
              htmlFor={`${idPrefix}create-team-org-description`}
              dangerouslySetInnerHTML={{
                __html: formatBody('description-optional'),
              }}
            />
            <textarea
              className={styles.textAreaInput}
              dir="auto"
              id={`${idPrefix}create-team-org-description`}
              name="desc"
              onChange={this.onChangeTeamDescription}
              tabIndex={0}
            />
          </>
        )}
        <PrimaryButton
          className={styles.submitButton}
          type="submit"
          size={ButtonSizes.WIDE}
          disabled={!displayName.trim() || isSubmitting}
          testId={TeamTestIds.CreateTeamSubmitButton}
        >
          {formatBody('create')}
        </PrimaryButton>
        <hr className={styles.separator} />
        <p className={classNames(styles.quiet, styles.bottomParagraph)}>
          {formatBody(
            teamType === TeamType.Enterprise
              ? 'a-team-is-a-group-of-boards-and-people-owned'
              : 'a-team-is-a-group-of-boards-and-people',
          )}
        </p>
        {!dontUpsell && teamType !== TeamType.Enterprise && (
          <p className={classNames(styles.quiet, styles.bottomParagraph)}>
            <span
              dangerouslySetInnerHTML={{
                __html: `${formatBody(
                  'business-class-gives-your-organization-more',
                )} `, // intentional space appended
              }}
            />
            <a
              className={styles.quiet}
              onClick={this.onBusinessLinkClick}
              href="/business-class"
              target="_blank"
              rel="noopener noreferrer"
            >
              {formatBody('learn-more')}
            </a>
          </p>
        )}
      </form>
    );
  }
}

export default CreateTeam;
